import { gql, useQuery } from '@apollo/client'
import { ForbiddenError } from '../components/ErrorBoundary'
import { StructParams, StructureBase, getChildType, QueryStructParams } from '../lib/structures'
import type { Timeline } from '../types/Timeline'

interface CanvassingDataForChildren {
  current: number
  last7days: number
  last30days: number
  milestone: number
  target: number
  attendance?: number
}

interface RegistrationData {
  population: number
}

interface CanvassingDataForSelf extends CanvassingDataForChildren {
  historic: Timeline<number>
  milestones: Timeline<number>
}

export interface StructureWithCanvassing extends StructureWithCanvassingBase {
  canvassing: CanvassingDataForSelf
  parents: StructureBase[]
  children: StructureWithCanvassingBase[]
}

interface StructureWithCanvassingBase extends StructureBase {
  canvassing: CanvassingDataForChildren
  registration: RegistrationData
  labels: string[]
}

interface CanvassingResult {
  structure: StructureWithCanvassing
}

const query = gql`
    query getCanvassingData($type: String!, $code: String!, $childType: String!, $uniquePeriod: UniquePeriods) {
    structure(type: $type, code: $code) {
        type
        code
        name
        labels
        responsiblePersons { firstname surname displayname }
        userHasAccess
        structureHasAssignedWards
        activity {
            attendance {
                current
                last30days
                last7days
            }
        }
        canvassing(uniquePeriod: $uniquePeriod) {
            current
            last30days
            last7days
        }
        registration {
            population
        }
        parents { type code name userHasAccess }
        children(type: $childType) {
        type
        code
        name
        labels
        responsiblePersons { firstname surname displayname }
        userHasAccess
        structureHasAssignedWards
        activity {
            attendance {
                current
                last30days
                last7days
            }
        }
        canvassing(uniquePeriod: $uniquePeriod) {
            current
            last30days
            last7days
        }
        registration {
            population
        }
        }
    }
    }
`

export function useAction ({ type, code, childType: ct, uniquePeriod = 'daily' }: StructParams): StructureWithCanvassing | undefined {
  const childType = ct ?? getChildType({ type, code })
  const { error, data } = useQuery<CanvassingResult, QueryStructParams>(
    query, { variables: { type, code, childType, uniquePeriod } }
  )

  if (error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN') {
    throw new ForbiddenError('this structure')
  }
  if (error != null) throw error

  return data?.structure
}
